import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import {
  ChevronLeft,
  Calendar,
  Users,
  Moon,
  MapPin,
  CheckCircle,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

/**
 * @param {{
 *   hotel: {
 *     image: string;
 *     name: string;
 *     area: string;
 *     room_type: string;
 *     price_per_night: number;
 *   };
 *   bookingData: {
 *     checkin: Date;
 *     checkout: Date;
 *     guests: number;
 *     nights: number;
 *     totalAmount: number;
 *   };
 *   onBack: () => void;
 *   onConfirm: (data: any) => void;
 *   onReturnToMenu?: () => void;
 * }} props
 */
export default function AccommodationCheckoutView({
  hotel,
  bookingData,
  onBack,
  onConfirm,
  onReturnToMenu,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);

  const { checkin, checkout, guests, nights, totalAmount } = bookingData;

  const handleConfirm = () => {
    onConfirm({ hotel, checkin, checkout, guests, nights, totalAmount });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={onBack}
            className="gap-2 -ml-2 text-[#0B2421] hover:bg-transparent hover:text-[#D4AF37] hover:underline"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          {onReturnToMenu && (
            <Button
              variant="outline"
              onClick={onReturnToMenu}
              className="gap-2 bg-[#0B2421] hover:bg-[#091E1B] text-white hover:text-white border-none"
            >
              Return to Booking Menu
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-[#0B2421] mb-2">
            Confirm Your Accommodation
          </h1>
          <p className="text-gray-500 mb-8">Review your booking details</p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="p-8 bg-white border border-gray-200 shadow-xl">
              {/* Hotel Info */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={hotel.image}
                    alt={hotel.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-[#0B2421] text-sm leading-tight mb-1">
                    {hotel.name}
                  </h3>
                  <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
                    <MapPin className="h-3 w-3 text-[#D4AF37]" />
                    <span>{hotel.area}</span>
                  </div>
                  <div className="text-xs text-gray-400">{hotel.room_type}</div>
                </div>
              </div>

              {/* Info Grid */}
              <div className="border-t border-gray-200 pt-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0B2421]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-5 w-5 text-[#0B2421]" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Check-in</div>
                      <div className="font-semibold text-[#0B2421] text-sm">
                        {format(checkin, "dd MMM yyyy")}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0B2421]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-5 w-5 text-[#0B2421]" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Check-out</div>
                      <div className="font-semibold text-[#0B2421] text-sm">
                        {format(checkout, "dd MMM yyyy")}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0B2421]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Moon className="h-5 w-5 text-[#0B2421]" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Nights</div>
                      <div className="font-semibold text-[#0B2421] text-sm">{nights}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0B2421]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="h-5 w-5 text-[#0B2421]" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Guests</div>
                      <div className="font-semibold text-[#0B2421] text-sm">{guests}</div>
                    </div>
                  </div>
                </div>

                {/* Price */}
                <div className="border-t border-gray-200 pt-4 space-y-2 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">
                      SGD {hotel.price_per_night} × {nights} night{nights > 1 ? "s" : ""}
                    </span>
                    <span className="font-medium text-[#0B2421]">SGD {totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-200">
                    <span className="text-[#0B2421]">Subtotal</span>
                    <span className="text-[#D4AF37]">SGD {totalAmount.toFixed(2)}</span>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-gray-500">
                    💡 Payment will be processed after you complete selecting all services
                  </p>
                </div>

                <Button
                  onClick={handleConfirm}
                  className="w-full h-14 bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold text-lg"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Confirm Accommodation Selection
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
